/**
 * ListenButton — browser-native TTS ("Listen to this article").
 *
 * Uses `window.speechSynthesis` — free, offline, works in 40+ languages
 * out of the box on modern browsers. Zero server cost. Upgrade path is
 * a drop-in swap to OpenAI TTS via /api/content-engine/articles/:id/tts.
 *
 * Behavior:
 *   Play / Pause / Resume / Stop / Restart + playback speed
 *   Follows the caller's `lang` prop so the correct voice is picked.
 *   Falls back gracefully when speechSynthesis is unsupported.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Play, Pause, StopCircle, RotateCcw, Volume2, Loader2, Sparkles } from "lucide-react";
import { API } from "@/lib/api";

const SPEEDS = [0.75, 1, 1.25, 1.5];

function stripHtml(s) {
  return (s || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

function pickVoice(lang) {
  if (typeof window === "undefined" || !window.speechSynthesis) return null;
  const voices = window.speechSynthesis.getVoices() || [];
  if (!voices.length) return null;
  const l = (lang || "en").toLowerCase();
  // Try exact match, then prefix match (e.g. ml → ml-IN).
  return (
    voices.find((v) => v.lang.toLowerCase() === l) ||
    voices.find((v) => v.lang.toLowerCase().startsWith(l.split("-")[0] + "-")) ||
    voices.find((v) => v.lang.toLowerCase().startsWith(l.split("-")[0])) ||
    null
  );
}

export default function ListenButton({ text, lang = "en", articleId = null, testid = "sp-listen" }) {
  const [state, setState] = useState("idle"); // idle | playing | paused
  const [speed, setSpeed] = useState(1);
  const [supported, setSupported] = useState(true);
  const [voiceReady, setVoiceReady] = useState(false);
  const [serverAudio, setServerAudio] = useState(null);   // {url, voice} when the server has cached studio audio
  const utterRef = useRef(null);
  const audioRef = useRef(null);

  // Check if server-cached studio audio exists for (article, lang).
  useEffect(() => {
    setServerAudio(null);
    if (!articleId) return;
    let cancelled = false;
    axios.get(`${API}/content-engine/articles/${articleId}/audio-status`, { params: { lang }, timeout: 4000 })
      .then((r) => { if (!cancelled && r.data?.ready) setServerAudio({ url: `${API}/content-engine/audio/${articleId}/${lang}`, voice: r.data.voice }); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [articleId, lang]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.speechSynthesis) {
      setSupported(false);
      return;
    }
    // Voices load asynchronously in some browsers.
    const check = () => setVoiceReady((window.speechSynthesis.getVoices() || []).length > 0);
    check();
    window.speechSynthesis.addEventListener("voiceschanged", check);
    return () => {
      window.speechSynthesis.removeEventListener("voiceschanged", check);
      window.speechSynthesis.cancel();
    };
  }, []);

  const stop = useCallback(() => {
    // Stop server audio if playing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    // Stop browser TTS
    if (supported) {
      window.speechSynthesis.cancel();
      utterRef.current = null;
    }
    setState("idle");
  }, [supported]);

  // If lang/text change mid-play, stop so caller can restart.
  useEffect(() => { stop(); }, [lang, text, stop]);

  const play = useCallback(() => {
    const cleaned = stripHtml(text);
    if (!cleaned && !serverAudio) return;

    // Prefer server studio audio when available.
    if (serverAudio?.url) {
      if (!audioRef.current) audioRef.current = new Audio();
      const a = audioRef.current;
      a.src = serverAudio.url;
      a.playbackRate = speed;
      a.onended = () => setState("idle");
      a.onerror = () => setState("idle");
      a.play().then(() => setState("playing")).catch(() => setState("idle"));
      return;
    }

    // Fallback — browser speechSynthesis.
    if (!supported) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(cleaned);
    u.lang = lang;
    u.rate = speed;
    const v = pickVoice(lang);
    if (v) u.voice = v;
    u.onend = () => { setState("idle"); utterRef.current = null; };
    u.onerror = () => { setState("idle"); utterRef.current = null; };
    utterRef.current = u;
    window.speechSynthesis.speak(u);
    setState("playing");
  }, [supported, text, lang, speed, serverAudio]);

  const togglePause = useCallback(() => {
    if (serverAudio?.url && audioRef.current) {
      if (state === "playing") { audioRef.current.pause(); setState("paused"); }
      else if (state === "paused") { audioRef.current.play(); setState("playing"); }
      return;
    }
    if (!supported) return;
    if (state === "playing") {
      window.speechSynthesis.pause();
      setState("paused");
    } else if (state === "paused") {
      window.speechSynthesis.resume();
      setState("playing");
    }
  }, [supported, state, serverAudio]);

  if (!supported && !serverAudio) {
    return (
      <div className="text-xs text-slate-500 italic" data-testid={`${testid}-unsupported`}>
        Listen isn't supported on this browser.
      </div>
    );
  }

  const isStudio = !!serverAudio;

  return (
    <div
      data-testid={testid}
      className="inline-flex items-center gap-1 rounded-full bg-white ring-1 ring-slate-200 px-1.5 py-1 shadow-sm"
    >
      {isStudio && state === "idle" && (
        <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5" title="Studio-quality audio from OpenAI TTS">
          <Sparkles className="w-2.5 h-2.5" /> Studio
        </span>
      )}
      {state === "idle" && (
        <button
          type="button"
          onClick={play}
          disabled={!serverAudio && !voiceReady}
          data-testid={`${testid}-play`}
          className="inline-flex items-center gap-1.5 rounded-full bg-violet-600 hover:bg-violet-700 text-white px-3.5 py-1.5 text-xs font-semibold transition-colors disabled:opacity-60"
          aria-label="Listen to this article"
        >
          {(serverAudio || voiceReady) ? <Play className="w-3.5 h-3.5" /> : <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          Listen
        </button>
      )}
      {state !== "idle" && (
        <>
          <button
            type="button"
            onClick={togglePause}
            data-testid={`${testid}-pause`}
            className="inline-flex items-center gap-1.5 rounded-full bg-violet-600 hover:bg-violet-700 text-white px-3 py-1.5 text-xs font-semibold"
            aria-label={state === "paused" ? "Resume" : "Pause"}
          >
            {state === "paused" ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
            {state === "paused" ? "Resume" : "Pause"}
          </button>
          <button
            type="button"
            onClick={stop}
            data-testid={`${testid}-stop`}
            className="inline-flex items-center rounded-full ring-1 ring-slate-300 hover:ring-slate-400 text-slate-700 px-2.5 py-1.5 text-xs font-semibold"
            aria-label="Stop"
          >
            <StopCircle className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => { stop(); setTimeout(play, 60); }}
            data-testid={`${testid}-restart`}
            className="inline-flex items-center rounded-full ring-1 ring-slate-300 hover:ring-slate-400 text-slate-700 px-2.5 py-1.5 text-xs font-semibold"
            aria-label="Restart"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        </>
      )}
      <div className="flex items-center gap-0.5 ml-1 pl-1 border-l border-slate-200" role="group" aria-label="Playback speed">
        <Volume2 className="w-3 h-3 text-slate-400 ml-1" aria-hidden />
        {SPEEDS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => {
              setSpeed(s);
              if (state !== "idle") { stop(); setTimeout(play, 60); }
            }}
            data-testid={`${testid}-speed-${String(s).replace(".", "-")}`}
            className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold transition-colors ${speed === s ? "bg-violet-100 text-violet-700" : "text-slate-500 hover:text-slate-800"}`}
            aria-label={`${s}x speed`}
            aria-pressed={speed === s}
          >
            {s}×
          </button>
        ))}
      </div>
    </div>
  );
}
